import { useState } from 'react';
import { ExternalLink, Loader2 } from 'lucide-react';
import { getClassJoinInfo, API_BASE_URL } from '../services/api';
import ZoomMeetingEmbed from './ZoomMeetingEmbed';

// Secure "Join Class" flow: calls /api/classes/:id/join with the user's
// Bearer token. The raw meeting link is never present in this page's HTML or
// in any network response the student can inspect before clicking.
//
//  - Zoom classes (mode: 'embed', only when ZOOM_SDK_KEY/SECRET are
//    configured on the backend and the link is a standard zoom.us join URL)
//    render fully embedded in-page via ZoomMeetingEmbed — no redirect, no new
//    tab, the address bar never leaves our domain.
//  - Everything else (mode: 'redirect') opens a short-lived, single-purpose
//    redirect URL in a new tab. That tab's address bar will show the real
//    meeting URL once the browser follows the redirect — this can't be
//    avoided for Google Meet/Teams/Classroom (no embeddable SDK exists for
//    them), only for Zoom.
const JoinClassButton = ({ classId, token, displayName, disabled, className }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [embedInfo, setEmbedInfo] = useState(null);

  const handleJoin = async () => {
    if (!token) {
      setError('Please log in to join this class.');
      return;
    }
    setError('');
    setLoading(true);

    // Open the tab synchronously, inside the click handler, so browsers
    // treat it as a direct result of the user's click rather than an
    // unsolicited popup once the network request below resolves. Closed
    // again immediately if it turns out we don't need it (embed mode).
    const newTab = window.open('', '_blank');

    try {
      const { data } = await getClassJoinInfo(classId, token);

      if (data.mode === 'embed') {
        newTab?.close();
        setEmbedInfo(data);
        return;
      }

      // data.redirectUrl is a relative path on the backend (e.g. /api/classes/redirect/<token>).
      // In production frontend+backend share an origin, but in local dev they're on
      // different ports (3000 vs 5000), so it must be resolved against the API origin
      // rather than left relative (which would otherwise hit the frontend dev server).
      const fullRedirectUrl = `${API_BASE_URL.replace(/\/api$/, '')}${data.redirectUrl}`;
      if (newTab) {
        newTab.location.href = fullRedirectUrl;
      } else {
        // Popups fully blocked even for the synchronous open - fall back
        // to navigating the current tab so the join still works.
        window.location.href = fullRedirectUrl;
      }
    } catch (err) {
      newTab?.close();
      setError(err.response?.data?.message || 'Could not join class. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <button
        onClick={handleJoin}
        disabled={disabled || loading}
        className={className || `flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold transition-all ${
          disabled ? 'bg-white/5 text-gray-500 pointer-events-none' : 'bg-accentBlue/10 border border-accentBlue/30 text-accentBlue hover:bg-accentBlue hover:text-black'
        }`}
      >
        {loading ? <Loader2 size={14} className="animate-spin" /> : <ExternalLink size={14} />}
        {loading ? 'Joining...' : 'Join'}
      </button>
      {error && <p className="text-xs text-red-400 mt-1.5">{error}</p>}

      {embedInfo && (
        <ZoomMeetingEmbed
          meetingNumber={embedInfo.meetingNumber}
          signature={embedInfo.signature}
          password={embedInfo.password}
          userName={displayName}
          onClose={() => setEmbedInfo(null)}
        />
      )}
    </div>
  );
};

export default JoinClassButton;
